import Select from 'react-select'
import { Field, Formik, Form, FieldArray } from 'formik'
import * as React from 'react'
import * as Yup from 'yup'
import Trash from '../../icons/ui/TrashGrey'
import {
  IHotspot,
  ISelectOption,
  IServiceDesignatedHotspot,
  IServiceDesignatedHotspotLocationProps
} from '../../../types/index'
import UIMessages from 'src/utils/ui-messages'
import Point from 'src/components/icons/ui/Point'
import ServiceDesignatedPinLocationModal from 'src/components/ServiceDesignatedPinLocationModal'
import AutoCompleteLocations from 'src/components/google-components/AutoCompleteLocations'
import { searchLocation, getPlaceDetails } from 'src/utils/location'
import DeleteHotspotModal from './modal/DeleteHotspotModal'
import ErrorBar from 'src/components/ui/ErrorBar'
import { MoonLoader } from 'react-spinners'

enum TO_DELETE {
  NONE,
  ZONE,
  HOTSPOT
}
interface IDesignatedHotspotState {
  isAdding: boolean
  isDeleting: boolean
  isSaving: boolean
  isOpen: boolean
  index: number
  locations: IServiceDesignatedHotspot[]
  locationIdsToUpdate: string[]
  locationToPin: IServiceDesignatedHotspot | null
  autoCompleteLocations: IServiceDesignatedHotspot[]
  selectedHotspotZone: string
  searchTerm: string
  debounceTimeoutId: NodeJS.Timeout | undefined
  toDelete: TO_DELETE
  deleteModalIsOpen: boolean
  toDeleteId: string | null
  deleteModalConfirmMessage: string
  newHotspot: IHotspot | null
  error: {
    message: string
    updatedAt: number
  }
}

class ServiceEditDesignatedHotspots extends React.Component<
  IServiceDesignatedHotspotLocationProps,
  IDesignatedHotspotState
> {
  private validationSchema: any
  private debounceTimeoutId: NodeJS.Timeout | undefined

  constructor(props: IServiceDesignatedHotspotLocationProps) {
    super(props)
    this.loadYupValidation()

    this.state = {
      isAdding: false,
      isDeleting: false,
      isSaving: false,
      locations: this.props.designatedHotspotLocations,
      locationIdsToUpdate: [],
      locationToPin: null,
      isOpen: false,
      deleteModalIsOpen: false,
      index: -1,
      autoCompleteLocations: [],
      selectedHotspotZone: '',
      searchTerm: '',
      debounceTimeoutId: undefined,
      toDelete: TO_DELETE.NONE,
      toDeleteId: null,
      deleteModalConfirmMessage: '',
      newHotspot: null,
      error: {
        message: '',
        updatedAt: Date.now()
      }
    }
  }

  public loadYupValidation() {
    Yup.addMethod(Yup.mixed, 'unique', function(message, mapper = (a: any) => a) {
      return this.test('unique', message, (list: any) => {
        const valueArr = list.map((item: any) => item.name)
        const hasDuplicate = valueArr.some((item: any, idx: any) => {
          return valueArr.indexOf(item) !== idx
        })
        if (hasDuplicate) {
          return false
        }
        return true
      })
    })

    this.validationSchema = Yup.object().shape({
      designatedLocation: Yup.object().shape({
        hotspots: Yup.array()
          .of(
            Yup.object().shape({
              name: Yup.string()
                .min(2, 'name too short')
                .required('required'),
              fullAddress: Yup.string()
                .min(4, 'address too short')
                .required('required')
            })
          )
          .unique('Duplicate hotspot name is not allowed!', (a: any) => a)
      })
    })
  }
  public componentDidMount() {
    if (this.props.serviceId !== undefined && this.props.serviceId !== null) {
      this.props.getHotspotsList(this.props.serviceId)
    }
    const { designatedHotspotsEnabled } = this.props
    if (designatedHotspotsEnabled !== undefined && !designatedHotspotsEnabled) {
      this.setState({
        error: {
          message: 'Designated hotspots is disabled for this service.',
          updatedAt: Date.now()
        }
      })
    }
  }

  public componentDidUpdate(
    prevProps: Readonly<IServiceDesignatedHotspotLocationProps>,
    prevState: Readonly<IDesignatedHotspotState>
  ): void {
    if (prevState.error.message !== this.state.error.message) {
      this.setState({
        error: {
          message: '',
          updatedAt: Date.now()
        }
      })
    }

    if (JSON.stringify(this.props.designatedHotspotLocations) !== JSON.stringify(this.state.locations)) {
      this.setState(state => {
        const newState = { ...state }
        newState.locations = prevProps.designatedHotspotLocations
        newState.selectedHotspotZone =
          this.props.designatedHotspotLocations.length > 0 ? this.props.designatedHotspotLocations[0].id : ''
        return newState
      })
    }
    if (
      this.props.serviceId !== prevProps.serviceId &&
      (this.props.serviceId !== undefined && this.props.serviceId !== null)
    ) {
      this.refreshState()
    }
  }
  public componentWillUnmount() {
    this.setState({
      error: {
        message: '',
        updatedAt: Date.now()
      }
    })
  }
  public handleSubmit = (values: any) => {
    this.props.setLoading(true)
    this.props.saveLocations(values.designatedLocation, this.props.serviceId).then(() => {
      this.refreshState()
      this.props.setLoading(false)
    })
  }

  public deleteDesignatedZone = () => {
    if (this.state.selectedHotspotZone) {
      this.setState({
        deleteModalIsOpen: true,
        deleteModalConfirmMessage: UIMessages.common.deleteDesignatedZoneModalMessage,
        toDelete: TO_DELETE.ZONE,
        toDeleteId: this.state.selectedHotspotZone
      })
    }
  }

  public render() {
    const {
      newHotspot,
      error,
      error: { message }
    } = this.state

    const designatedHotspotLocations = [...this.state.locations]

    const hotspotsSelectValues: ISelectOption[] = designatedHotspotLocations.map(item => ({
      label: item.name,
      value: item.id
    }))

    const selected = designatedHotspotLocations.find(item => item.id === this.state.selectedHotspotZone)

    const defaultValue = this.getDefaultHotspot(selected, designatedHotspotLocations)
    let selectDefaultValue
    if (selected) {
      selectDefaultValue = {
        label: selected.name,
        value: selected.id
      }
    } else if (defaultValue !== null) {
      selectDefaultValue = {
        label: defaultValue.name,
        value: defaultValue.id
      }
    } else {
      selectDefaultValue = null
    }
    const initValues: any = {
      designatedLocation: this.getDefaultHotspot(selected, designatedHotspotLocations)
    }

    return (
      <div>
        {message && <ErrorBar error={error} />}
        {this.state.deleteModalIsOpen && (
          <DeleteHotspotModal
            message={this.state.deleteModalConfirmMessage}
            onClose={this.closeDeleteHotspotModal}
            onDelete={this.onDeleteHotspotModal}
            isDeleting={this.state.isDeleting}
          />
        )}

        <div className='flex justify-end m2 '>
          <div className='col-3'>
            <div>
              <label htmlFor=''>Select Zone</label>
            </div>
            <div>
              <Select
                name='serviceLevel'
                value={selectDefaultValue}
                onChange={this.onChangeSelectHotspotZone}
                options={hotspotsSelectValues}
              />
            </div>
          </div>
          <div className='col-1 ml1 self-end cursor--pointer' onClick={this.deleteDesignatedZone}>
            <Trash className='icon--22x22' />
          </div>
        </div>

        <Formik
          ref={this.props.formRef}
          enableReinitialize={true}
          initialValues={initValues}
          validationSchema={this.validationSchema}
          onSubmit={e => this.handleSubmit(e)}
        >
          {({
            values,
            touched,
            setFieldValue,
            errors
          }: {
            values: any
            setFieldValue: any
            errors: any
            touched: any
          }) => (
            <Form>
              {this.state.isOpen && (
                <ServiceDesignatedPinLocationModal
                  zoom={this.props.zoom}
                  bounds={this.props.bounds}
                  location={this.state.locationToPin!}
                  locations={this.state.locations}
                  onCancel={this.onCancel}
                  saveLocation={this.onModalLocationSaved}
                />
              )}
              {values.designatedLocation && (
                <FieldArray name='designatedLocation.hotspots'>
                  {({ push, remove }) => (
                    <>
                      <div className='ml3'>
                        <div className='flex'>
                          <div className='col-3'>
                            <span className='h5 font-grey2'>{UIMessages.common.nameLabel}</span>
                          </div>

                          <div className='col-3'>
                            <span className='h5 font-grey2'>{UIMessages.common.address}</span>
                          </div>

                          <div className='col-4'>
                            <span className='h5 font-grey2'>{UIMessages.common.pin}</span>
                          </div>
                          <div className='col-2'>&nbsp; </div>
                        </div>

                        <div>
                          <div className='bt-light-grey4'>&nbsp;</div>
                        </div>
                        {values.designatedLocation.hotspots.map((hotspot: IHotspot, i: number) => (
                          <div className='flex mb1' key={`hotspot-${i}`}>
                            <div className='col-2 mr1'>
                              <Field
                                className='p1 mr3'
                                style={{
                                  marginTop: 'auto',
                                  marginBottom: 'auto'
                                }}
                                type='text'
                                placeholder='Name'
                                name={`designatedLocation.hotspots[${i}].name`}
                                onChange={(e: any) => this.onNameChange(e, i)}
                                autoComplete='off'
                                disabled={false}
                              />
                              <div className='error'>
                                <span className='h6 font-red'>
                                  {errors.designatedLocation &&
                                    errors.designatedLocation.hotspots &&
                                    errors.designatedLocation.hotspots[i] &&
                                    errors.designatedLocation.hotspots[i].name}
                                </span>{' '}
                                &nbsp;
                              </div>
                            </div>

                            <div className='col-3 mr1'>
                              <div className='flex flex-row relative'>
                                <Field
                                  className='p1'
                                  style={{
                                    marginTop: 'auto',
                                    marginBottom: 'auto'
                                  }}
                                  type='text'
                                  name={`designatedLocation.hotspots[${i}].fullAddress`}
                                  onChange={(e: any) => this.onAddressChange(e, i)}
                                  placeholder='Add Address'
                                  autoComplete='off'
                                  id={`${i}`}
                                />
                                {this.state.index === i && (
                                  <div className='left0 auto-complete-location absolute top-100 bg-light-grey2 z1 cursor--pointer'>
                                    {this.state.autoCompleteLocations.map(
                                      (item: IServiceDesignatedHotspot, i: number) => (
                                        <AutoCompleteLocations
                                          key={`${hotspot.googlePlaceId}-${i}`}
                                          onSelect={this.onLocationSelect}
                                          item={item}
                                        />
                                      )
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className='error'>
                                <span className='h6 font-red'>
                                  {errors.designatedLocation &&
                                    errors.designatedLocation.hotspots &&
                                    errors.designatedLocation.hotspots[i] &&
                                    errors.designatedLocation.hotspots[i].fullAddress}
                                </span>{' '}
                                &nbsp;
                              </div>
                            </div>

                            <div className='col-4'>
                              <div className='flex flex-row relative'>
                                <button type='button' onClick={() => this.openModal(values.designatedLocation, i)}>
                                  <Point className='svg-blue' />
                                </button>
                                <input
                                  className='p1 mr3'
                                  type='text'
                                  style={{
                                    marginTop: 'auto',
                                    marginBottom: 'auto'
                                  }}
                                  value={`${hotspot.coordinates.lat},${hotspot.coordinates.lng}`}
                                  disabled={true}
                                />
                              </div>
                            </div>
                            <div className='col-1 pt1'>
                              <div
                                className='cursor--pointer'
                                style={{
                                  marginTop: 'auto',
                                  marginBottom: 'auto'
                                }}
                                onClick={() => this.onDeleteHotspot(hotspot)}
                              >
                                <Trash className='icon--22x22' />
                              </div>
                            </div>
                          </div>
                        ))}

                        <div className='flex mb1' key={`hotspot-error`}>
                          <div className='col-4 flex flex-row'>
                            <span className='font-red'>
                              {errors &&
                              errors.designatedLocation &&
                              typeof errors.designatedLocation.hotspots === 'string'
                                ? errors.designatedLocation.hotspots
                                : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </FieldArray>
              )}
            </Form>
          )}
        </Formik>

        <div className='ml2 pl2 bold font-small mt1 mb4 flex'>
          {this.state.isAdding && (
            <span className='flex g-gap10 items-center justify-center'>
              <MoonLoader sizeUnit={'px'} size={13} color={'#000000'} loading={this.state.isAdding} />
              Creating Hotspot
            </span>
          )}
          {!this.state.isAdding && (
            <a href='#' onClick={this.onCreateHotspot}>
              + Add Hotspot
            </a>
          )}
        </div>
      </div>
    )
  }

  private getDefaultHotspot(
    selected: IServiceDesignatedHotspot | undefined,
    designatedHotspotLocations: IServiceDesignatedHotspot[]
  ) {
    if (selected) {
      return selected
    }
    if (designatedHotspotLocations.length > 0) {
      return designatedHotspotLocations[0]
    } else {
      return null
    }
  }

  private onCreateHotspot = () => {
    this.setState({
      isAdding: true
    })
    const zoneIndex = this.state.locations.findIndex(item => item.id === this.state.selectedHotspotZone)
    if (zoneIndex < 0) {
      console.log(`Designated zone not found: ${this.state.selectedHotspotZone}`)
      return
    }
    const hotspotsCopy = [...this.state.locations]
    const newHotSpot: IHotspot = {
      designatedHotspotsZoneId: hotspotsCopy[zoneIndex].id!,
      name: '',
      fullAddress: '',
      coordinates: {
        lat: 0,
        lng: 0
      }
    }
    hotspotsCopy[zoneIndex].hotspots.push(newHotSpot)
    this.setState({
      newHotspot: newHotSpot,
      locations: [...hotspotsCopy],
      isAdding: false
    })
  }

  private onChangeSelectHotspotZone = (evt: any) => {
    this.setState({
      selectedHotspotZone: evt.value
    })
  }

  // Triggers: [onchange of address search]
  private onLocationSelect = (selectedLocation: any) => async () => {
    const placeDetails = await getPlaceDetails(selectedLocation.googlePlaceId)

    const currentStateLocations = [...this.state.locations]
    const editedLocationIndex = currentStateLocations.findIndex(item => item.id === this.state.selectedHotspotZone)
    if (editedLocationIndex >= 0) {
      currentStateLocations[editedLocationIndex].hotspots[this.state.index].fullAddress = selectedLocation.fullAddress
      currentStateLocations[editedLocationIndex].hotspots[this.state.index].coordinates = placeDetails.coordinates
    }

    this.setState({
      locations: currentStateLocations,
      autoCompleteLocations: [] // Clear the auto-complete suggestions
    })

    this.clearActions()
  }

  private onAddressChange = async (event: any, i: number): Promise<void | boolean> => {
    event.persist()

    const address = event.target.value

    const selectedZoneIndex = this.state.locations.findIndex(item => item.id === this.state.selectedHotspotZone)
    const zones = [...this.state.locations]
    zones[selectedZoneIndex].hotspots[Number(event.target.id)].fullAddress = address

    this.setState({
      index: i,
      locations: [...zones]
    })

    // optimized call for google place api
    this.searchGoogleSuggestedLocations(address)
  }

  private onNameChange = async (event: any, i: number): Promise<void | boolean> => {
    const name = event.target.value

    const selectedZoneIndex = this.state.locations.findIndex(item => item.id === this.state.selectedHotspotZone)
    const zones = [...this.state.locations]
    zones[selectedZoneIndex].hotspots[i].name = name

    this.setState({
      index: i,
      locations: [...zones]
    })
  }

  // activated only for every 500milliseconds and being reset until the user stop typing
  private searchGoogleSuggestedLocations = async (address: string) => {
    if (this.debounceTimeoutId) {
      clearTimeout(this.debounceTimeoutId)
    }

    this.debounceTimeoutId = setTimeout(async () => {
      const autoCompleteLocations = await searchLocation({ input: address })
      this.setState({
        autoCompleteLocations
      })
    }, 500)
  }

  private clearActions = () => {
    this.setState({
      autoCompleteLocations: []
    })
  }
  private onDeleteHotspot = (hotspot: IHotspot) => {
    this.setState({
      toDeleteId: hotspot.id || null,
      deleteModalIsOpen: true,
      deleteModalConfirmMessage: UIMessages.common.deleteDesignatedZoneHotspotModalMessage,
      toDelete: TO_DELETE.HOTSPOT
    })
  }

  private closeDeleteHotspotModal = () => {
    this.clearState()
  }

  private clearState = () => {
    this.setState({
      toDeleteId: null,
      deleteModalIsOpen: false,
      deleteModalConfirmMessage: '',
      toDelete: TO_DELETE.NONE
    })
  }
  private refreshState = () => {
    this.props.getHotspotsList(this.props.serviceId!).then(() => {
      this.setState({
        toDeleteId: null,
        deleteModalIsOpen: false,
        deleteModalConfirmMessage: '',
        toDelete: TO_DELETE.NONE,
        locations: this.props.designatedHotspotLocations,
        newHotspot: null
      })
    })
  }
  private deleteRefreshState = () => {
    this.props.getHotspotsList(this.props.serviceId!).then(() => {
      this.setState({
        toDeleteId: null,
        deleteModalIsOpen: false,
        deleteModalConfirmMessage: '',
        toDelete: TO_DELETE.NONE,
        locations: this.props.designatedHotspotLocations,
        newHotspot: null,
        selectedHotspotZone: ''
      })
    })
  }

  private onDeleteHotspotModal = () => {
    if (this.state.toDeleteId) {
      if (this.state.toDelete === TO_DELETE.HOTSPOT) {
        this.setState({
          isDeleting: true
        })
        return this.props.deleteHotspot(this.state.toDeleteId).then(() => {
          this.setState({
            isDeleting: false
          })
          this.refreshState()
        })
      }
      if (this.state.toDelete === TO_DELETE.ZONE) {
        this.setState({
          isDeleting: true
        })
        return this.props.deleteDesignatedZone(this.state.selectedHotspotZone).then(() => {
          this.setState({
            isDeleting: false
          })
          this.deleteRefreshState()
        })
      }
    }

    this.refreshState()
    return false
  }

  private openModal = (location: IServiceDesignatedHotspot, index: number) => {
    if (!location.coordinates || (location.coordinates.lat === 0 && location.coordinates.lng === 0)) {
      return
    }
    location.coordinates = location.hotspots[index].coordinates

    this.setState({
      isOpen: true,
      index,
      locationToPin: location
    })
  }

  private onCancel = () => {
    this.setState({
      isOpen: false,
      newHotspot: null
    })
  }

  private onModalLocationSaved = (location: IServiceDesignatedHotspot) => {
    this.onUpdateLocation(location, this.state.index)
  }

  private onUpdateLocation = (location: IServiceDesignatedHotspot, index: number) => {
    this.setState(state => {
      const newState = { ...state }
      const selectedZoneIndex = newState.locations.findIndex(item => item.id === this.state.selectedHotspotZone)
      if (selectedZoneIndex >= 0) {
        newState.locations[selectedZoneIndex].hotspots[index].coordinates = {
          lat: location.coordinates.lat,
          lng: location.coordinates.lng
        }
      }
      newState.isOpen = false
      newState.locationToPin = null
      return newState
    })
  }
}

export default ServiceEditDesignatedHotspots



// WEBPACK FOOTER //
// ./src/components/services/edit/ServiceEditDesignatedHotspots.tsx